import { Link } from 'wouter'
import { LegalLayout, LegalSection } from './LegalLayout'

export function Terminos() {
  return (
    <LegalLayout title="Términos y Condiciones del Servicio" lastUpdated="5 de junio de 2026">
      <p className="text-muted-foreground">
        Estos Términos y Condiciones (en adelante, los <strong>«Términos»</strong>) regulan el acceso
        y uso de la plataforma <strong>Facil Control</strong> y su aplicación móvil complementaria.
        Al registrarte, contratar una licencia o utilizar el servicio, declaras haber leído,
        entendido y aceptado estos Términos en su totalidad. Si no estás de acuerdo, debes
        abstenerte de utilizar el servicio.
      </p>

      <LegalSection title="1. Identificación del prestador">
        <p>
          El servicio es prestado por <strong>PFP App Service LLC</strong>, RUT <strong>76.075.598-2</strong>,
          con domicilio en Presidente Riesco 5711, 7561114, comuna de Las Condes, Región Metropolitana,
          Chile (en adelante, <strong>«PFP App Service»</strong>, <strong>«nosotros»</strong> o el
          <strong> «Prestador»</strong>). Para cualquier comunicación relacionada con estos Términos
          puedes escribir a{' '}
          <a href="mailto:contacto@facilcontrol.cl" className="text-primary hover:underline">
            contacto@facilcontrol.cl
          </a>.
        </p>
      </LegalSection>

      <LegalSection title="2. Definiciones">
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong>Plataforma:</strong> el panel web de Facil Control para la gestión de seguridad
            laboral y cumplimiento de contratistas (DS 76, Ley N° 16.744).
          </li>
          <li>
            <strong>Aplicación o App:</strong> la aplicación móvil complementaria de la Plataforma,
            de uso principalmente fuera de línea (offline) con sincronización cuando exista conexión.
          </li>
          <li>
            <strong>Usuario:</strong> toda persona que crea una cuenta en la Plataforma.
          </li>
          <li>
            <strong>Empresa:</strong> la persona jurídica titular de la cuenta que contrata el
            servicio y bajo cuya organización se gestionan trabajadores, contratos y documentos.
          </li>
          <li>
            <strong>Trabajador:</strong> persona natural vinculada a una Empresa cuyos datos y
            documentos de seguridad se gestionan en el servicio.
          </li>
          <li>
            <strong>Licencia:</strong> autorización de uso, sujeta a pago, que habilita funciones
            específicas (Aplicación móvil y Capacitaciones con IA).
          </li>
          <li>
            <strong>Capacitaciones con IA:</strong> el módulo que genera contenido de capacitación y
            cuestionarios mediante sistemas de inteligencia artificial.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="3. Descripción del servicio">
        <p>
          Facil Control es un sistema de gestión de seguridad laboral y cumplimiento de contratistas
          orientado al mercado chileno. Permite administrar empresas, contratos, trabajadores,
          recursos (EPP, vehículos, maquinaria), capacitaciones e inspecciones, así como generar
          y firmar digitalmente documentos que sirven como evidencia de cumplimiento normativo.
        </p>
        <p>
          La Aplicación móvil es un <strong>complemento</strong> de la Plataforma, diseñada para
          operar la mayor parte del tiempo sin conexión y sincronizar la información con la
          Plataforma cuando exista conectividad. La disponibilidad y el alcance de la sincronización
          pueden evolucionar con el desarrollo del producto.
        </p>
      </LegalSection>

      <LegalSection title="4. Registro y cuenta">
        <p>
          Para usar el servicio debes crear una cuenta proporcionando información veraz, completa y
          actualizada, incluyendo tu RUT, que constituye tu credencial de acceso. Eres responsable de
          mantener la confidencialidad de tus credenciales y de toda actividad realizada bajo tu
          cuenta. Debes notificarnos de inmediato ante cualquier uso no autorizado.
        </p>
        <p>
          El servicio está dirigido exclusivamente a personas mayores de edad en el contexto de una
          relación laboral o comercial. No está orientado a menores de edad.
        </p>
      </LegalSection>

      <LegalSection title="5. Modelo de licencias y acceso">
        <p>
          El registro de un Usuario es gratuito, pero <strong>no otorga por sí solo</strong> acceso a
          todas las funciones. En particular:
        </p>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            El acceso a las <strong>Capacitaciones con IA</strong> requiere una Licencia vigente.
          </li>
          <li>
            El acceso funcional a la <strong>Aplicación móvil</strong> requiere una Licencia vigente.
            La Aplicación puede descargarse libremente, pero solo los Trabajadores de una Empresa
            que cuente con Licencia pagada podrán acceder a sus funcionalidades.
          </li>
        </ul>
        <p>
          Las Licencias se asignan por la Empresa a sus Usuarios y Trabajadores según los cupos
          contratados. PFP App Service podrá modificar el alcance de los planes y sus funciones,
          informando con antelación razonable cuando ello afecte funciones ya contratadas.
        </p>
      </LegalSection>

      <LegalSection title="6. Pagos, facturación y suspensión">
        <p>
          Las Licencias se facturan según el plan contratado. El no pago oportuno faculta a PFP App
          Service para suspender o restringir el acceso a las funciones licenciadas (Aplicación móvil
          y Capacitaciones con IA) hasta regularizar la situación, sin perjuicio del cobro de las
          sumas adeudadas. La información ya registrada como evidencia de cumplimiento se conservará
          conforme a la ley y a la Política de Privacidad, aun durante una suspensión.
        </p>
      </LegalSection>

      <LegalSection title="7. Capacitaciones con Inteligencia Artificial">
        <p>
          El módulo de Capacitaciones con IA genera contenidos y cuestionarios de forma automatizada
          mediante sistemas de terceros. Estos contenidos son una <strong>herramienta de apoyo</strong> y
          pueden contener imprecisiones; <strong>no sustituyen</strong> la asesoría de un experto en
          prevención de riesgos ni el juicio profesional de la Empresa. La Empresa es responsable de
          revisar, validar y adecuar los contenidos antes de utilizarlos como parte de su gestión de
          seguridad. El detalle del tratamiento de datos en este módulo se describe en la{' '}
          <Link href="/privacidad" className="text-primary hover:underline">
            Política de Privacidad
          </Link>.
        </p>
      </LegalSection>

      <LegalSection title="8. Obligaciones y responsabilidad del Usuario y la Empresa">
        <p>
          El Usuario y la Empresa se obligan a utilizar el servicio conforme a la ley, a estos
          Términos y a la buena fe. En particular, se obligan a:
        </p>
        <ul className="list-disc space-y-2 pl-5">
          <li>Ingresar información veraz y mantenerla actualizada.</li>
          <li>
            Contar con base de licitud suficiente para incorporar y tratar los datos de sus
            Trabajadores y terceros en la Plataforma, e informar a dichas personas según corresponda.
          </li>
          <li>
            No utilizar el servicio para fines ilícitos, ni vulnerar derechos de terceros, ni
            intentar acceder de forma no autorizada a la Plataforma.
          </li>
          <li>Responder por el uso que sus Usuarios y Trabajadores hagan del servicio.</li>
        </ul>
        <p>
          Respecto de los datos personales de sus Trabajadores, la Empresa actúa como responsable del
          tratamiento y PFP App Service como encargado, según se detalla en la Política de Privacidad.
        </p>
      </LegalSection>

      <LegalSection title="9. Propiedad intelectual">
        <p>
          La Plataforma, la Aplicación, sus marcas, diseños, código y contenidos propios son de
          titularidad de PFP App Service o de sus licenciantes, y están protegidos por la legislación
          aplicable. Estos Términos no transfieren al Usuario ningún derecho de propiedad intelectual,
          sino únicamente una autorización de uso limitada, revocable y no exclusiva mientras la
          Licencia esté vigente. Los datos y documentos ingresados por la Empresa siguen siendo de su
          titularidad.
        </p>
      </LegalSection>

      <LegalSection title="10. Disponibilidad y mantenimiento">
        <p>
          PFP App Service procurará mantener el servicio disponible de forma continua, pero no
          garantiza una operación libre de interrupciones. Podrán realizarse tareas de mantenimiento,
          actualización o corrección que afecten temporalmente la disponibilidad. Dado el carácter
          offline-first de la Aplicación, ciertas funciones podrán seguir operando localmente y
          sincronizarse al restablecerse la conexión.
        </p>
      </LegalSection>

      <LegalSection title="11. Limitación de responsabilidad">
        <p>
          En la máxima medida permitida por la ley, PFP App Service no será responsable por daños
          indirectos, lucro cesante o pérdida de datos derivados del uso o imposibilidad de uso del
          servicio, ni por decisiones que la Empresa adopte basándose en contenidos generados por IA.
          La responsabilidad total de PFP App Service, de existir, se limitará a los montos
          efectivamente pagados por la Empresa por el servicio durante los doce meses anteriores al
          hecho que origine la responsabilidad. Ninguna disposición de estos Términos excluye
          responsabilidades que no puedan limitarse conforme a la ley chilena.
        </p>
      </LegalSection>

      <LegalSection title="12. Protección de datos personales">
        <p>
          El tratamiento de datos personales se rige por nuestra{' '}
          <Link href="/privacidad" className="text-primary hover:underline">
            Política de Privacidad
          </Link>
          , que forma parte integrante de estos Términos. Al aceptar estos Términos, declaras conocer
          dicha política.
        </p>
      </LegalSection>

      <LegalSection title="13. Vigencia y terminación">
        <p>
          Estos Términos se mantienen vigentes mientras utilices el servicio. Puedes terminar la
          relación cerrando tu cuenta. PFP App Service podrá suspender o terminar el acceso ante
          incumplimientos graves de estos Términos o de la ley. La terminación no afecta la
          conservación de la información que deba mantenerse como evidencia de cumplimiento normativo,
          conforme a la ley y a la Política de Privacidad.
        </p>
      </LegalSection>

      <LegalSection title="14. Modificaciones">
        <p>
          PFP App Service podrá modificar estos Términos para reflejar cambios legales, técnicos o del
          servicio. Las modificaciones se informarán por medios razonables y entrarán en vigencia
          desde su publicación. El uso continuado del servicio tras la entrada en vigencia implica la
          aceptación de los Términos modificados.
        </p>
      </LegalSection>

      <LegalSection title="15. Ley aplicable y jurisdicción">
        <p>
          Estos Términos se rigen por las leyes de la República de Chile. Cualquier controversia será
          sometida a los tribunales ordinarios de justicia con asiento en la ciudad de Santiago, sin
          perjuicio de los derechos que la ley reconozca a los consumidores y titulares de datos.
        </p>
      </LegalSection>

      <LegalSection title="16. Contacto">
        <p>
          Ante cualquier consulta sobre estos Términos, puedes contactarnos en{' '}
          <a href="mailto:contacto@facilcontrol.cl" className="text-primary hover:underline">
            contacto@facilcontrol.cl
          </a>{' '}
          o por correo a Presidente Riesco 5711, 7561114, Las Condes, Región Metropolitana, Chile.
        </p>
      </LegalSection>
    </LegalLayout>
  )
}
